import React from "react";
import { useProfileUpdateContext } from "../context/profile_update_context";
import { User, Mail, ArrowRight, KeyRound, CheckCircle } from "lucide-react";
import "../../login_v2/styles/animation.css";

const ProfileUpdateComp = () => {
  const {
    name,
    email,
    otp,
    errorMessage,
    nameErrorMessage,
    emailErrorMessage,
    otpErrorMessage,
    isProcessing,
    isFormDisabled,
    isOtpSent,
    isOtpVerified,
    isEmailChanged,
    handleNameChange,
    handleEmailChange,
    handleOtpChange,
    handleSendOtp,
    handleVerifyOtp,
    handleProfileUpdate,
    otpTimer,
  } = useProfileUpdateContext();

  return (
    <div className="md:min-h-screen text-white flex items-center justify-center md:px-4 md:py-8">
      <div className="w-full max-w-md animate-fade-in">
        <div className="gradient-border p-0.5 shadow-xl shadow-red-950/10">
          <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-lg overflow-hidden relative">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>

            <div className="px-6 py-8 md:px-10 md:py-10">
              <div className="text-center mb-10">
                <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-6">
                  <User size={26} className="text-white animate-pulse-soft" />
                </div>
                <h1 className="text-3xl font-bold mb-1">Update Profile</h1>
                <p className="text-pink-300 mt-2">
                  Update your profile information
                </p>
              </div>

              {errorMessage && (
                <div className="mb-6 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-white text-sm">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleProfileUpdate} className="space-y-6">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-pink-200"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User size={18} className="z-[1] text-pink-400" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      value={name}
                      onChange={handleNameChange}
                      className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  {nameErrorMessage && (
                    <p className="text-red-400 text-xs mt-1">
                      {nameErrorMessage}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-pink-200"
                  >
                    Email
                  </label>
                  <div className="relative flex gap-2">
                    <div className="flex-1 relative">
                      <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={18} className="z-[1] text-pink-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={handleEmailChange}
                        className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                        placeholder="name@example.com"
                      />
                    </div>
                    {isEmailChanged && !isOtpVerified && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isProcessing || !email || emailErrorMessage || otpTimer > 0}
                        className="px-4 py-3 bg-gradient-to-r from-red-700 to-red-900 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpTimer > 0 ? `Resend OTP (${otpTimer}s)` : "Send OTP"}
                      </button>
                    )}
                  </div>
                  {emailErrorMessage && (
                    <p className="text-red-400 text-xs mt-1">
                      {emailErrorMessage}
                    </p>
                  )}
                  {isEmailChanged && !isOtpVerified && (
                    <p className="text-yellow-400 text-xs mt-1">
                      Email has been changed. Please verify with OTP.
                    </p>
                  )}
                </div>

                {isOtpSent && (
                  <div className="space-y-2">
                    <label
                      htmlFor="otp"
                      className="block text-sm font-medium text-pink-200"
                    >
                      OTP
                    </label>
                    <div className="relative flex gap-2">
                      <div className="flex-1 relative">
                        <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <KeyRound size={18} className="z-[1] text-pink-400" />
                        </div>
                        <input
                          id="otp"
                          name="otp"
                          type="text"
                          inputMode="numeric"
                          required
                          value={otp}
                          onChange={handleOtpChange}
                          className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                          placeholder="Enter OTP"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isProcessing || !otp || otpErrorMessage}
                        className="px-4 py-3 bg-gradient-to-r from-red-700 to-red-900 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Verify OTP
                      </button>
                    </div>
                    {otpErrorMessage && (
                      <p className="text-red-400 text-xs mt-1">
                        {otpErrorMessage}
                      </p>
                    )}
                    {isOtpVerified && (
                      <div className="flex items-center text-green-400 text-sm mt-1">
                        <CheckCircle size={16} className="mr-1" />
                        <span>OTP verified successfully</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isFormDisabled}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-700 via-red-800 to-red-900 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md active:translate-y-0.5 active:shadow-none"
                  >
                    <span>Update Profile</span>
                    <ArrowRight size={18} />
                  </button>
                  {isEmailChanged && !isOtpVerified && (
                    <p className="text-red-400 text-xs mt-2 text-center">
                      Please verify your email change with OTP before updating your profile
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileUpdateComp;
